import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { ParagraphOutputText, TextItem, LabelKeyValueItem } from "@/model";
import { toSpliced, withArray } from "@/util/arrayUtil";
import { getNewTextItem } from "@/util/helper/paragraphHelper";
import { useScenarioAutocompletes } from "@/hook";
import Button from "@/component/Button";
import { Textarea } from "@/component/input";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";

import { ParagraphInputsProps } from "../model";


type TextOptionType = ParagraphOutputText["variant"] | "default";

const TEXT_TYPE_OPTIONS: LabelKeyValueItem<TextOptionType>[] = [
    { value: "default", labelKey: "i04045" },
    { value: "error", labelKey: "i04046" }
];

const ParagraphOutputTextCardInputs: React.FC<ParagraphInputsProps<ParagraphOutputText>> = (props) => {
    const { paragraph, onChange } = props;

    const intl = useIntl();

    const { variableAutocomplete } = useScenarioAutocompletes();

    const [texts, setTexts] = useState<TextItem[]>([]);

    const onChangeTextType = (value: TextOptionType) => {
        onChange({
            ...paragraph,
            variant: value === "default" ? undefined : value
        });
    };

    const onChangeText = (newTexts: TextItem[]) => {
        setTexts(newTexts);
        onChange({
            ...paragraph,
            data: newTexts.map(text => ({ ...text, text: text.text.trim() }))
        });
    };

    useEffect(() => {
        if (Array.isArray(paragraph.data)) {
            if (paragraph.data.length > 0) {
                setTexts(paragraph.data);
            } else {
                setTexts([getNewTextItem()]);
            }
        } else {
            if (paragraph.data && typeof paragraph.data === "object" && "text" in paragraph.data) {
                setTexts([paragraph.data]);
            } else {
                setTexts([getNewTextItem()]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (<>
        <fieldset role="radiogroup"
                  className="contents"
        >
            <legend className="label-input-grid-label field-label">
                {intl.formatMessage({ id: "i04048" })}
            </legend>
            <div className="flex gap-4 items-center">
                {TEXT_TYPE_OPTIONS.map(option => (
                    <label key={option.value}
                           className="w-20 flex gap-1 items-center cursor-pointer"
                    >
                        <input type="radio"
                               className="input-radio-chat42 size-4"
                               name={`${paragraph.id}-text-type`}
                               checked={(paragraph.variant ?? "default") === option.value}
                               onChange={() => onChangeTextType(option.value)}
                        />
                        <div className="text-[13px] font-[400] text-black-500">
                            {intl.formatMessage({ id: option.labelKey })}
                        </div>
                    </label>
                ))}
            </div>
        </fieldset>

        <div className="col-span-2 flex flex-col gap-1.5">
            <div className="h-6 flex gap-1 items-center">
                <div className="field-label">
                    {intl.formatMessage({ id: "b0056" })}
                </div>
                <Button data-testid="paragraph-text-add"
                        variant="text-transparent"
                        aria-label={intl.formatMessage({ id: "b0009" })}
                        onClick={() => onChangeText([...texts, getNewTextItem()])}
                >
                    <AddIcon className="size-3" />
                </Button>
            </div>
            <div className="flex flex-col gap-1">
                {texts.length === 0 && (
                    <div className="text-invalid">
                        {intl.formatMessage({ id: "i04047" })}
                    </div>
                )}
                {texts.map((text, index) => (
                    <div key={`${index}-${texts.length}`}
                         className="flex items-center gap-0.5"
                    >
                        <Textarea size="sm"
                                  required
                                  rows={1}
                                  autocompletes={variableAutocomplete}
                                  defaultValue={text.text}
                                  placeholder={intl.formatMessage({ id: "i04050" })}
                                  onChange={value => onChangeText(withArray(texts, index, { ...text, text: value }))}
                        />
                        <button type="button"
                                className="p-2 self-start flex justify-center text-red-500 shrink-0"
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={() => onChangeText(toSpliced(texts, index, 1))}
                        >
                            <DeleteIcon className="size-4" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    </>);
};

export default ParagraphOutputTextCardInputs;
